import { ConfigService, FullNamePipe } from '@abovo/shared-angular-utils';
import { ApiGetResponse, ClientContact, CreateApprovalReviewer, MenuConfig, Target } from '@abovo/shared-models';
import { SkeletonLoaderModule, TargetDisplayComponent } from '@abovo/shared-ui-components';
import { ClientContactsService } from '@abovo/webvis-data-access';
import { CommonModule } from '@angular/common';
import { HttpParams, httpResource } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { TranslatePipe } from '@ngx-translate/core';
import { startWith, switchMap } from 'rxjs';
import { ReviewerRoleSlugs } from '../constants/approval-request.constants';
import { constructApprovalReviewer } from '../helpers/approval-reviewer.helper';

@Component({
   selector: 'webvis-feature-approval-reviewers-section',
   templateUrl: './approval-reviewers-section.component.html',
   changeDetection: ChangeDetectionStrategy.OnPush,
   imports: [TranslatePipe, MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger, CommonModule, FullNamePipe, TargetDisplayComponent, SkeletonLoaderModule],
})
export class ApprovalReviewersSectionComponent {
   private readonly configService = inject(ConfigService);

   clientId = input<number>();
   control = input.required<FormControl<CreateApprovalReviewer[]>>();

   clientContacts = httpResource<ApiGetResponse<ClientContact>>(
      () => {
         const clientId = this.clientId();

         // httpResource skips the request when the request function returns undefined.
         if (!clientId) {
            return undefined;
         }

         return {
            url: `${this.configService.get('app.api_url_v2')}/${ClientContactsService.url}`,
            params: new HttpParams({ fromObject: { client_ids: [clientId] } }),
         };
      },
      { defaultValue: { result: [] } },
   );

   reviewers = toSignal(toObservable(this.control).pipe(switchMap((control) => control.valueChanges.pipe(startWith(control.value)))), { initialValue: [] });
   requiredReviewers = computed(() => this.reviewers().filter((r) => r.role_type?.slug === ReviewerRoleSlugs.required || !r.role_type));
   optionalReviewers = computed(() => this.reviewers().filter((r) => r.role_type?.slug === ReviewerRoleSlugs.optional));

   requiredReviewError = signal('');

   private requiredReviewErrorEffect = effect((onCleanup) => {
      const control = this.control();
      const subscription = control.events.subscribe(() => {
         untracked(() => this.requiredReviewError.set(control.hasError('requiredReview') && control.touched ? 'validation.requiredReview' : ''));
      });
      onCleanup(() => subscription.unsubscribe());
   });
   availableContacts = computed(() => {
      const reviewerIds = this.reviewers().map((reviewer) => reviewer.user?.id);
      const contacts = this.clientContacts.value().result;
      return contacts.filter((contact) => contact.user_id && !reviewerIds.includes(contact.user_id));
   });

   readonly removeMenuConfig: MenuConfig<Target> = [
      {
         label: 'general.remove',
         icon: ['far', 'trash-alt'],
         action: (employee) => {
            if (employee.id) {
               this.removeReviewer(employee.id);
            }
         },
      },
   ];

   addContact(contact: ClientContact, role_type_slug: ReviewerRoleSlugs) {
      if (!contact.user_id) {
         return;
      }
      const current = this.control().value;
      const newReviewer: CreateApprovalReviewer = constructApprovalReviewer(contact, role_type_slug);
      this.control().setValue([...current, newReviewer]);
      this.control().markAsDirty();
   }

   removeReviewer(userId: number) {
      const current = this.control().value;
      this.control().setValue(current.filter((r) => r.user?.id !== userId));
      this.control().markAsDirty();
   }
}
